import React, { useState } from 'react';
import { theme, Steps } from 'antd';
import { SiteSurveyHeaderContainer, StyledEPModal } from './SiteSurveyHeader.styles';
import { CloseOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { EPButton } from '@/components';
import { useResponsive } from '@/hooks/useResponsive';
import { useStores } from '@/hooks/use-stores';

const { useToken } = theme;

interface SiteSurveyHeaderProps {
  currentStep?: number;
  totalSteps?: number;
  onBack?: () => void;
  onClose?: () => void;
  onSaveAndExit?: () => Promise<void>;
}

export const SiteSurveyHeader: React.FC<SiteSurveyHeaderProps> = ({
  currentStep = 1,
  totalSteps = 3,
  onBack,
  onClose,
  onSaveAndExit,
}) => {
  const { token } = useToken();
  const { t } = useTranslation();
  const { isLg, isXl } = useResponsive();
  const [showCloseModal, setCloseModal] = useState(false);
  const { siteSurveyStore } = useStores();

  const isDesktop = isLg || isXl;

  const items = [
    { title: t('customerInformationStepLabel', 'Confirm customer info') },
    { title: t('siteConfigurationStepLabel', 'Define Site configuration') },
    { title: t('roomConfigurationStepLabel', 'Assign assets') },
  ];

  const isHomePage = currentStep === 0;
  const shouldShowModal = currentStep >= 1 && currentStep <= totalSteps;

  const showBackButton = currentStep >= 1 && onBack;

  const customDot = (dot: React.ReactNode) => {
    if (isHomePage) {
      return <span className="ant-steps-icon-dot" />;
    }
    return dot;
  };

  const handleCloseClick = (): void => {
    if (shouldShowModal) {
      setCloseModal(true);
    } else {
      onClose();
    }
  };

  const onDiscardModal = async (): Promise<void> => {
    if (currentStep > 1) {
      await siteSurveyStore.deleteForm(siteSurveyStore.formId);
    }

    setCloseModal(false);
    onClose();
  };

  const handleSaveAndExitClick = async (): Promise<void> => {
    if (onSaveAndExit) {
      onSaveAndExit();
    } else {
      console.error('onSaveAndExit prop is not provided');
      if (onClose) {
        onClose();
      }
    }
    setCloseModal(false);
  };

  interface CloseModalProps {
    width?: string;
    height?: string;
    centered?: boolean;
  }

  const CloseModal = ({ width = '520px', height = '172px', centered = true }: CloseModalProps) => {
    return (
      <StyledEPModal
        open={showCloseModal}
        onCancel={() => setCloseModal(false)}
        width={width}
        height={height}
        centered={centered}
      >
        <div>
          <p className="close-modal-title">{t('saveExitSiteSurvey', 'Save & Exit the site survey?')}</p>
          <p className="close-modal-body">
            {t(
              'closeModalBody',
              'Your progress has been saved. You can continue editing this station anytime from the station list on the main screen.',
            )}
          </p>
          <div className="close-button-container">
            <EPButton
              type="primary"
              onClick={() => {
                onDiscardModal();
              }}
              className="close-button"
              size="large"
            >
              {t('discardAndExit', 'Discard & Exit')}{' '}
            </EPButton>
            <EPButton type="primary" onClick={handleSaveAndExitClick} className="close-button" size="large">
              {t('saveAndExit', 'Save & Exit')}
            </EPButton>
          </div>
        </div>
      </StyledEPModal>
    );
  };

  return (
    <SiteSurveyHeaderContainer theme={{ token }} isHomePage={isHomePage}>
      {!isDesktop && (
        <div className="mobile-header-row">
          <div className="left-section">
            {showBackButton && (
              <EPButton type="text" onClick={onBack} className="back-button" size="large">
                <ArrowLeftOutlined />
                <div className="button-text">{t('backButton', 'Back')}</div>
              </EPButton>
            )}
          </div>
          <div className="right-section">
            <CloseModal width="380px" height="220px" centered={false} />

            <EPButton type="text" onClick={handleCloseClick} className="close-button" size="large">
              <div className="button-inner-container">
                <div>
                  <div className="close-text">{t('closeButton', 'Close')}</div>
                  <CloseOutlined />
                </div>
              </div>
            </EPButton>
          </div>
        </div>
      )}

      <div className="main-content-row">
        {isDesktop && (
          <div className="left-section">
            {showBackButton && (
              <EPButton type="text" onClick={onBack} className="back-button" size="large">
                <ArrowLeftOutlined />
                <div className="button-text">{t('backButton', 'Back')}</div>
              </EPButton>
            )}
          </div>
        )}

        <div className="steps-container">
          {!isDesktop ? (
            <Steps
              progressDot={isHomePage ? customDot : true}
              current={currentStep === 0 ? -1 : currentStep > totalSteps ? totalSteps : currentStep - 1}
              items={items}
              className="site-survey-steps"
              size="small"
              labelPlacement="vertical"
              direction="horizontal"
            />
          ) : (
            <Steps
              current={currentStep === 0 ? -1 : currentStep > totalSteps ? totalSteps : currentStep - 1}
              items={items}
              className="site-survey-steps"
              size="small"
              labelPlacement="horizontal"
              direction="horizontal"
            />
          )}
        </div>

        {isDesktop && (
          <div className="right-section">
            <CloseModal centered={true} height={'190px'} />

            <EPButton type="text" onClick={handleCloseClick} className="close-button" size="large">
              <div className="button-inner-container">
                <div>
                  <div className="close-text">{t('closeButton', 'Close')}</div>
                  <CloseOutlined />
                </div>
              </div>
            </EPButton>
          </div>
        )}
      </div>
    </SiteSurveyHeaderContainer>
  );
};
